import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import authAxios from "setups/axios";
import { toastMessage } from "utils/toastMessage";
import moment from "moment";
import { PageHeader, TablePager } from "_shared/ui";

// ── helpers ────────────────────────────────────────────────────────────────

const today = new Date();
const DEFAULT_START = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const DEFAULT_END = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

const fmtTime = (v) => (v ? moment(v).format("DD MMM YY, hh:mm A") : "—");

const VISIT_TYPES = ["dealer", "prospect", "influencer", "zkp"];
const VISIT_LABELS = ["Dealer", "Prospect", "Influencer", "ZKP Customer"];
const PROSPECT_STATUSES = ["", "Pending_SO", "Approved", "Rejected", "In-Review"];
const ZONES = ["NZ", "EZ", "NP", "TZ", "SZ", "CZ", "WZ"];

// ── shared small components ────────────────────────────────────────────────

const Loading = () => (
  <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
    <CircularProgress color="primary" />
  </Box>
);

const EmptyRow = ({ cols, message = "No data found." }) => (
  <TableRow>
    <TableCell colSpan={cols} align="center">
      <Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>
        {message}
      </Typography>
    </TableCell>
  </TableRow>
);

// Map a prospect status to a theme semantic color for its Chip.
const PROSPECT_STATUS_CFG = {
  Approved: { label: "Approved", color: "success" },
  Rejected: { label: "Rejected", color: "error" },
  "In-Review": { label: "In-Review", color: "info" },
  Pending_SO: { label: "Pending SO", color: "warning" },
};

const ProspectStatusChip = ({ status }) => {
  const cfg = PROSPECT_STATUS_CFG[status] ?? { label: status ?? "Unknown", color: "default" };
  return (
    <Chip
      size="small"
      label={cfg.label}
      color={cfg.color}
      variant={cfg.color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: 500, fontSize: "0.7rem" }}
    />
  );
};

// Themed toolbar wrapper for a tab's filters/search controls.
const Toolbar = ({ children }) => (
  <Paper sx={{ p: 2, mb: 2, border: 1, borderColor: "divider" }}>
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
      {children}
    </Box>
  </Paper>
);

// ── Tab 1 — OTR Users ─────────────────────────────────────────────────────

const UsersTab = ({ onSelectUser }) => {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotal]    = useState(1);
  const [search, setSearch]       = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [zone, setZone]           = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const doFetch = async () => {
      try {
        const p = new URLSearchParams({ page, limit: 15 });
        if (debSearch) p.set("search", debSearch);
        if (zone)      p.set("zone", zone);
        const res = await authAxios().get(`/api/v1/admin/otr/users?${p}`);
        if (!cancelled) {
          setData(res.data?.data ?? []);
          setTotal(res.data?.totalPage ?? 1);
        }
      } catch (e) {
        if (!cancelled)
          toastMessage(e?.response?.data?.message ?? "Failed to load OTR users", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [page, debSearch, zone]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebSearch(e.target.value), 400);
  };

  return (
    <Box>
      <Toolbar>
        <TextField
          size="small"
          placeholder="Search name / SAP ID"
          value={search}
          onChange={handleSearch}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Zone"
          value={zone}
          onChange={(e) => { setZone(e.target.value); setPage(1); }}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All Zones</MenuItem>
          {ZONES.map((z) => (
            <MenuItem key={z} value={z}>{z}</MenuItem>
          ))}
        </TextField>
      </Toolbar>

      {loading ? <Loading /> : (
        <TableContainer component={Paper} sx={{ border: 1, borderColor: "divider" }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Name", "SAP ID", "Zone", "Region", "Area",
                  "Dealer Visits", "Prospect Visits", "Influencer Visits",
                  "ZKP Visits", "Total Visits"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? data.map((u) => (
                <TableRow
                  key={u.SAP_ID}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onSelectUser(u.SAP_ID)}
                >
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.SAP_ID}</TableCell>
                  <TableCell>{u.zone ?? "—"}</TableCell>
                  <TableCell>{u.regionName ?? "—"}</TableCell>
                  <TableCell>{u.areaName ?? "—"}</TableCell>
                  <TableCell>{u.dealerVisits ?? 0}</TableCell>
                  <TableCell>{u.prospectVisits ?? 0}</TableCell>
                  <TableCell>{u.influencerVisits ?? 0}</TableCell>
                  <TableCell>{u.zkpVisits ?? 0}</TableCell>
                  <TableCell>{u.totalVisits ?? 0}</TableCell>
                </TableRow>
              )) : <EmptyRow cols={10} message="No OTR users found." />}
            </TableBody>
          </Table>
          <TablePager
            totalPages={totalPages}
            page={page}
            rowsPerPage={15}
            onPageChange={(p) => setPage(p)}
          />
        </TableContainer>
      )}
    </Box>
  );
};

// ── Tab 2 — Visit Details ─────────────────────────────────────────────────

const VisitsTab = ({ sapId, startDate, endDate }) => {
  const [visitTypeIdx, setVisitTypeIdx] = useState(0);
  const [data, setData]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotal]          = useState(1);

  useEffect(() => {
    setPage(1);
    setData([]);
  }, [visitTypeIdx, sapId, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const doFetch = async () => {
      try {
        const p = new URLSearchParams({
          visitType: VISIT_TYPES[visitTypeIdx],
          startDate,
          endDate,
          page,
          limit: 15,
        });
        if (sapId) p.set("SAP_ID", sapId);
        const res = await authAxios().get(`/api/v1/admin/otr/visits?${p}`);
        if (!cancelled) {
          setData(res.data?.data ?? []);
          setTotal(res.data?.totalPage ?? 1);
        }
      } catch (e) {
        if (!cancelled)
          toastMessage(e?.response?.data?.message ?? "Failed to load visits", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [visitTypeIdx, sapId, startDate, endDate, page]);

  return (
    <Box>
      <Tabs
        value={visitTypeIdx}
        onChange={(_, v) => setVisitTypeIdx(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {VISIT_LABELS.map((l, i) => (
          <Tab
            key={i}
            label={l}
            sx={{ textTransform: "none", fontWeight: visitTypeIdx === i ? 700 : 400, fontSize: "0.85rem" }}
          />
        ))}
      </Tabs>

      {loading ? <Loading /> : (
        <TableContainer component={Paper} sx={{ border: 1, borderColor: "divider" }}>
          <Table>
            <TableHead>
              <TableRow>
                {["OTR User", "Visit Type", "Start Time", "End Time",
                  "Completed", "Planned / Unplanned", "Remark"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? data.map((v, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{v.otrUser?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.otrUser?.SAP_ID}</Typography>
                  </TableCell>
                  <TableCell>{VISIT_LABELS[visitTypeIdx]}</TableCell>
                  <TableCell>{fmtTime(v.startTime)}</TableCell>
                  <TableCell>{fmtTime(v.endTime)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={v.completed ? "Yes" : "No"}
                      color={v.completed ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        visitTypeIdx === 0
                          ? (v.unplanned ? "Unplanned" : "Planned")
                          : (v.planned ? "Planned" : "Unplanned")
                      }
                    />
                  </TableCell>
                  <TableCell>{v.remark || "—"}</TableCell>
                </TableRow>
              )) : <EmptyRow cols={7} message={`No ${VISIT_LABELS[visitTypeIdx]} visits found.`} />}
            </TableBody>
          </Table>
          <TablePager
            totalPages={totalPages}
            page={page}
            rowsPerPage={15}
            onPageChange={(p) => setPage(p)}
          />
        </TableContainer>
      )}
    </Box>
  );
};

// ── Prospect helpers ──────────────────────────────────────────────────────

/**
 * Extracts the contact name and phone number from a prospect record.
 *
 * The API returns four distinct prospect shapes depending on `type`:
 *   FLEET          → ownerDetails[0].name / ownerDetails[0].phoneNumber
 *   INFLUENCER     → p.name / p.phoneNumber
 *   CUSTOMER       → customerDetails.name / customerDetails.phoneNumber
 *   PROSPECT /
 *   PROSPECT_DEALER→ basicInfo.firstPersonContact.name / .phoneNumber
 */
const getProspectNameAndPhone = (p) => {
  const type = p.type || p.prospectType;

  switch (type) {
    case "FLEET":
      return {
        name:  p.ownerDetails?.[0]?.name  || p.businessDetails?.companyName || "—",
        phone: p.ownerDetails?.[0]?.phoneNumber || p.businessDetails?.transportContactNumber || "—",
      };

    case "INFLUENCER":
      return {
        name:  p.name      || "—",
        phone: p.phoneNumber || "—",
      };

    case "CUSTOMER":
      return {
        name:  p.customerDetails?.name        || "—",
        phone: p.customerDetails?.phoneNumber || "—",
      };

    case "PROSPECT":
    case "PROSPECT_DEALER":
      return {
        name:  p.basicInfo?.firstPersonContact?.name        || "—",
        phone: p.basicInfo?.firstPersonContact?.phoneNumber || "—",
      };

    default:
      // Fallback: try every known location in order of likelihood
      return {
        name:
          p.name ||
          p.basicInfo?.firstPersonContact?.name ||
          p.ownerDetails?.[0]?.name ||
          p.customerDetails?.name ||
          "—",
        phone:
          p.phoneNumber ||
          p.basicInfo?.firstPersonContact?.phoneNumber ||
          p.ownerDetails?.[0]?.phoneNumber ||
          p.customerDetails?.phoneNumber ||
          "—",
      };
  }
};

// ── Tab 4 — Prospects ─────────────────────────────────────────────────────

const ProspectsTab = ({ sapId }) => {
  const [data, setData]            = useState([]);
  const [loading, setLoading]      = useState(false);
  const [page, setPage]            = useState(1);
  const [totalPages, setTotal]     = useState(1);
  const [status, setStatus]        = useState("");
  const [startDate, setStartDate]  = useState(DEFAULT_START);
  const [endDate, setEndDate]      = useState(DEFAULT_END);

  useEffect(() => {
    setPage(1);
    setData([]);
  }, [sapId, startDate, endDate, status]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const doFetch = async () => {
      try {
        const p = new URLSearchParams({ startDate, endDate, page, limit: 15 });
        if (sapId)  p.set("SAP_ID", sapId);
        if (status) p.set("status", status);
        const res = await authAxios().get(`/api/v1/admin/otr/prospects?${p}`);
        if (!cancelled) {
          setData(res.data?.data ?? []);
          setTotal(res.data?.totalPage ?? 1);
        }
      } catch (e) {
        if (!cancelled)
          toastMessage(e?.response?.data?.message ?? "Failed to load prospects", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [sapId, startDate, endDate, page, status]);

  return (
    <Box>
      <Toolbar>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {PROSPECT_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s || "All Status"}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Toolbar>

      {loading ? <Loading /> : (
        <TableContainer component={Paper} sx={{ border: 1, borderColor: "divider" }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Created By", "Name", "Mobile Number",
                  "Prospect Type", "Zone", "Status", "Created At"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? data.map((p, i) => {
                const { name, phone } = getProspectNameAndPhone(p);
                return (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{p.createdBy?.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.createdBy?.SAP_ID}</Typography>
                    </TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{phone}</TableCell>
                    <TableCell>{p.prospectType || p.businessInfo?.prospectType || "—"}</TableCell>
                    <TableCell>{p.zone || p.createdBy?.zone || "—"}</TableCell>
                    <TableCell><ProspectStatusChip status={p.prospectstatus} /></TableCell>
                    <TableCell>{fmtTime(p.createdAt)}</TableCell>
                  </TableRow>
                );
              }) : <EmptyRow cols={7} message="No prospects found." />}
            </TableBody>
          </Table>
          <TablePager
            totalPages={totalPages}
            page={page}
            rowsPerPage={15}
            onPageChange={(p) => setPage(p)}
          />
        </TableContainer>
      )}
    </Box>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────

const TAB_LABELS = ["OTR Users", "Visit Details", "Prospects"];

const Main = () => {
  const [tab, setTab]                     = useState(0);
  const [selectedSapId, setSelectedSapId] = useState("");
  const [startDate, setStartDate]         = useState(DEFAULT_START);
  const [endDate, setEndDate]             = useState(DEFAULT_END);

  const handleSelectUser = (sapId) => {
    setSelectedSapId(sapId);
    setTab(1); // Visit Details
  };

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="OTR Activity Dashboard"
        subtitle="OTR users, visit details, and prospects"
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {TAB_LABELS.map((l, i) => (
          <Tab
            key={i}
            label={l}
            sx={{ textTransform: "none", fontWeight: tab === i ? 700 : 400, fontSize: "0.9rem" }}
          />
        ))}
      </Tabs>

      {tab === 1 && (
        <Toolbar>
          <TextField
            size="small"
            label="SAP ID"
            placeholder="Filter by SAP ID"
            value={selectedSapId}
            onChange={(e) => setSelectedSapId(e.target.value)}
            sx={{ minWidth: 170 }}
          />
          <TextField
            size="small"
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {selectedSapId && (
            <Typography
              variant="caption"
              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline", alignSelf: "center" }}
              onClick={() => setSelectedSapId("")}
            >
              Clear SAP ID
            </Typography>
          )}
        </Toolbar>
      )}

      {tab === 0 && <UsersTab onSelectUser={handleSelectUser} />}
      {tab === 1 && <VisitsTab sapId={selectedSapId} startDate={startDate} endDate={endDate} />}
      {tab === 2 && <ProspectsTab sapId={selectedSapId} />}
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────

const AdminOTR = () => (
  <AccessController roles={["ADMINOTR"]}>
    <DashGridL2 Top={TopBar} Main={Main} />
    <Toaster />
  </AccessController>
);

export default AdminOTR;
